/**
 * <delivery-countdown> — counts down to a daily next-day-delivery cut-off time.
 *
 * Attributes:
 *   data-cutoff     — cut-off time in 24h "HH:MM" (in the market's local time)
 *   data-timezone   — IANA timezone the cut-off is defined in (e.g. "Europe/London")
 *   data-show-after — "true" to swap to the "after" copy once the cut-off passes;
 *                     otherwise the "before" copy stays while the timer resets.
 *   data-weekend-enabled — "true" to apply the no-Sunday-delivery weekend rule:
 *                     from Friday's cut-off through Sunday every order lands on
 *                     Monday, so hide the countdown and show the static
 *                     data-text-weekend message instead.
 *
 * Expects a descendant with [data-countdown-time] to write "HH:MM:SS" into.
 *
 * Before the cut-off it counts down to the cut-off and shows the "before" copy
 * (data-prefix-before / data-suffix-before). Once the cut-off passes, next-day
 * delivery no longer applies. When data-show-after is "true" it swaps to the
 * "after" copy (data-prefix-after / data-suffix-after) and counts down to midnight,
 * when the next-day window reopens. When it's "false" the "before" copy stays and
 * the timer rolls straight on to the following day's cut-off time.
 *
 * The weekend rule (when enabled) takes precedence over both: DPD doesn't deliver
 * on Sundays, so orders placed from Friday's cut-off until Monday all arrive Monday.
 */
class DeliveryCountdown extends HTMLElement {
  connectedCallback() {
    const [h, m] = (this.dataset.cutoff || '16:00').split(':');
    this.cutoffSeconds = (parseInt(h, 10) || 0) * 3600 + (parseInt(m, 10) || 0) * 60;
    this.timezone = this.dataset.timezone || undefined;
    this.showAfter = this.dataset.showAfter === 'true';
    this.weekendEnabled = this.dataset.weekendEnabled === 'true';
    this.output = this.querySelector('[data-countdown-time]');
    this.prefixEl = this.querySelector('[data-countdown-prefix]');
    this.suffixEl = this.querySelector('[data-countdown-suffix]');
    if (!this.output) return;

    this.update = this.update.bind(this);
    this.update();
    this.timer = setInterval(this.update, 1000);
  }

  /**
   * Keep an element's text in sync without clobbering it every tick.
   * @param {Element | null | undefined} el
   * @param {string | undefined} value
   */
  setText(el, value) {
    const text = value || '';
    if (el && el.textContent !== text) el.textContent = text;
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // Current wall-clock time of the target timezone: seconds since midnight + the
  // weekday (0 = Sunday … 6 = Saturday).
  now() {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        hour12: false,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(new Date());
      /** @param {string} type */
      const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
      let hours = parseInt(get('hour'), 10);
      if (hours === 24) hours = 0; // some engines emit "24" at midnight
      const seconds = hours * 3600 + parseInt(get('minute'), 10) * 60 + parseInt(get('second'), 10);
      /** @type {Record<string, number>} */
      const weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      const weekday = weekdays[get('weekday')] ?? new Date().getDay();
      return { seconds, weekday };
    } catch (e) {
      const d = new Date();
      return { seconds: d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(), weekday: d.getDay() };
    }
  }

  update() {
    if (!this.output) return;
    const { seconds: now, weekday } = this.now();
    const afterCutoff = now >= this.cutoffSeconds;

    // No Sunday delivery: Friday after cut-off, plus all of Saturday and Sunday,
    // every order arrives Monday. There's no meaningful same-day deadline during
    // this window (any order lands Monday), so show a static message with no timer.
    const inWeekendWindow =
      this.weekendEnabled && ((weekday === 5 && afterCutoff) || weekday === 6 || weekday === 0);

    if (inWeekendWindow) {
      this.setText(this.prefixEl, this.dataset.textWeekend);
      this.setText(this.suffixEl, '');
      this.output.toggleAttribute('hidden', true);
      return;
    }
    this.output.toggleAttribute('hidden', false);

    let remaining = 0;

    if (!afterCutoff) {
      // Before cut-off: next-day delivery applies. Count down to the cut-off.
      remaining = this.cutoffSeconds - now;
      this.setText(this.prefixEl, this.dataset.prefixBefore);
      this.setText(this.suffixEl, this.dataset.suffixBefore);
    } else if (this.showAfter) {
      // Past cut-off: count down to midnight, when the next-day window reopens,
      // and swap to the "after" copy.
      remaining = 86400 - now;
      this.setText(this.prefixEl, this.dataset.prefixAfter);
      this.setText(this.suffixEl, this.dataset.suffixAfter);
    } else {
      // Past cut-off: keep the "before" copy and roll straight on to tomorrow's
      // cut-off time, so the countdown never resets to midnight.
      remaining = 86400 - now + this.cutoffSeconds;
      this.setText(this.prefixEl, this.dataset.prefixBefore);
      this.setText(this.suffixEl, this.dataset.suffixBefore);
    }

    const hh = String(Math.floor(remaining / 3600)).padStart(2, '0');
    const mm = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    this.output.textContent = `${hh}:${mm}:${ss}`;
  }
}

if (!customElements.get('delivery-countdown')) {
  customElements.define('delivery-countdown', DeliveryCountdown);
}
